import { useMutation } from '@tanstack/react-query';

import { tokenStorage } from '@/lib/token-storage';

// Build-time constant injected by Vite via the `define` config in
// apps/web/vite.config.ts. In production/dev under Vite, this is statically
// replaced with the value of process.env.VITE_API_URL (or localhost fallback).
// Under Jest (no Vite), the constant is undefined and we use the fallback.
declare const __VITE_API_URL__: string | undefined;

const API_URL: string =
  typeof __VITE_API_URL__ === 'string' && __VITE_API_URL__
    ? __VITE_API_URL__
    : 'http://localhost:3001';

const ALLOWED_MIME = new Set(['image/png', 'image/jpeg', 'image/webp']);
const MAX_BYTES = 10 * 1024 * 1024;
const MAX_DIM = 4000;

export interface UploadAssetInput {
  file: File;
  bookId?: string;
}

export interface UploadAssetResult {
  src: string;
  assetUrl: string;
  width?: number;
  height?: number;
}

interface PresignResponse {
  assetId: string;
  key: string;
  uploadUrl: string;
  expiresAt: string;
}

interface CommitResponse {
  assetId: string;
  src: string;
}

async function sha256Hex(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

async function readDimensions(
  file: File,
): Promise<{ width: number; height: number }> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => {
        resolve();
      };
      img.onerror = () => {
        reject(new Error('Could not read image dimensions'));
      };
      img.src = url;
    });
    return { width: img.naturalWidth, height: img.naturalHeight };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function authHeader(): Record<string, string> {
  const token = tokenStorage.getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function useUploadAssetMutation() {
  return useMutation<UploadAssetResult, Error, UploadAssetInput>({
    mutationFn: async ({ file, bookId }) => {
      // 1) Local validation — fail fast before any network call.
      if (file.size > MAX_BYTES) {
        throw new Error(
          `File exceeds max size (${String(MAX_BYTES)} bytes)`,
        );
      }
      if (!ALLOWED_MIME.has(file.type)) {
        throw new Error(`Unsupported MIME type: ${file.type}`);
      }
      const dims = await readDimensions(file);
      if (dims.width > MAX_DIM || dims.height > MAX_DIM) {
        throw new Error(
          `Image dimensions exceed ${String(MAX_DIM)}×${String(MAX_DIM)}`,
        );
      }

      // 2) Presign — get a temporary S3 PUT URL from our API.
      const presignRes = await fetch(`${API_URL}/assets/presign`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          bookId,
          filename: file.name,
          mime: file.type,
          sizeBytes: file.size,
        }),
      });
      if (!presignRes.ok) {
        throw new Error(`Presign failed: ${String(presignRes.status)}`);
      }
      const presign = (await presignRes.json()) as PresignResponse;

      // 3) PUT directly to S3 (uploadUrl is a presigned URL — no auth header).
      const putRes = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: { 'content-type': file.type },
        body: file,
      });
      if (!putRes.ok) {
        throw new Error(`S3 PUT failed: ${String(putRes.status)}`);
      }

      // 4) Commit — confirm to API and get the canonical src.
      const sha256 = await sha256Hex(file);
      const commitRes = await fetch(`${API_URL}/assets/commit`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          assetId: presign.assetId,
          sha256,
          sizeBytes: file.size,
        }),
      });
      if (!commitRes.ok) {
        throw new Error(`Commit failed: ${String(commitRes.status)}`);
      }
      const commit = (await commitRes.json()) as CommitResponse;

      const signRes = await fetch(
        `${API_URL}/assets/${commit.assetId}/sign-url`,
        { headers: authHeader() },
      );
      if (!signRes.ok) {
        throw new Error(`Sign-url failed: ${String(signRes.status)}`);
      }
      const signed = (await signRes.json()) as { url: string };

      return {
        src: commit.src,
        assetUrl: signed.url,
        width: dims.width,
        height: dims.height,
      };
    },
  });
}
